import React, { useState, useEffect } from 'react';
import { Card, Table, Statistic, Row, Col, Spin, message, Tooltip, Button } from 'antd';
import { UserOutlined, WhatsAppOutlined, FileExcelOutlined } from '@ant-design/icons';
import { getDebtReport, exportDebtReport } from '../../api/reports';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import { formatMoney } from '../../utils/money';
import { raporIndir } from '../../utils/downloadBlob';
// Ham `905551112233` okunmuyor (frontend/CLAUDE.md → telefon kuralı).
import { formatPhone } from '../../utils/phone';

const DebtReportTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('report-debt', 20);
  const [page, setPage] = useState(1);
  // Liste sunucu tarafinda sayfali, bu yuzden siralama da SUNUCUDA yapiliyor
  // (istemci siralamasi yalnizca gorunen sayfayi sirasi ve yaniltirdi).
  const [sorter, setSorter] = useState({ field: null, order: null });

  const fetchData = (targetPage = page, perPage = persistedPageSize, sort = sorter) => {
    setLoading(true);
    getDebtReport({
      page: targetPage,
      per_page: perPage,
      sort_by: sort?.field || undefined,
      sort_direction: sort?.order === 'ascend' ? 'asc' : (sort?.order === 'descend' ? 'desc' : undefined),
    })
      .then(res => { setData(res.data); setPage(targetPage); })
      .catch(() => message.error('Borç raporu yüklenemedi.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(1, persistedPageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dosya SAYFALANMIYOR: sunucu `export=xlsx` geldiğinde sayfalamayı yok
  // sayıp tüm listeyi yazıyor (kullanıcının amacı listeyi personele
  // bölüştürmek — 20 satırlık dosya o işi görmez). Sıralama ise korunuyor,
  // böylece Excel ekranda görülen sırayla açılıyor.
  const excelIndir = async () => {
    setIndiriliyor(true);
    try {
      await raporIndir(exportDebtReport({
        sort_by: sorter?.field || undefined,
        sort_direction: sorter?.order === 'ascend' ? 'asc' : (sorter?.order === 'descend' ? 'desc' : undefined),
      }), 'borc-raporu.xlsx');
    } catch (e) {
      message.error(e?.message || 'Excel indirilemedi.');
    } finally {
      setIndiriliyor(false);
    }
  };

  // sorter: true = siralamayi sunucu yapiyor. Kolon adlari backend'deki beyaz
  // listeyle birebir ayni olmali.
  const sortProps = (field) => ({
    sorter: true,
    sortOrder: sorter.field === field ? sorter.order : null,
  });

  const columns = [
    { title: 'Öğrenci', dataIndex: 'student_name', ...sortProps('student_name') },
    { title: 'Şube', dataIndex: 'branch', ...sortProps('branch') },
    /*
      "ÖĞRENCİ TEL" SÜTUNU KALDIRILDI (18.10.2026).

      `students` tablosunda `phone` kolonu YOK — backend `$student->phone`
      okuyordu ve her satırda null dönüyordu, yani sütun yapısal olarak hiç
      dolmayacaktı. Öğrenciler 18 yaş altı; iletişim numarası velininki.

      "Veli Tel" ise veri olduğu hâlde boştu: backend ilişkiyi
      `parents:id,name,surname` ile yüklüyor, `phone` seçilmiyordu. Eksik
      kolon seçmek hata vermez, alanı sessizce null yapar.
    */
    { title: 'Veli', dataIndex: 'parent_name', ...sortProps('parent_name'), render: v => v || '—' },
    {
      title: 'Veli Tel',
      dataIndex: 'parent_phone',
      ...sortProps('parent_phone'),
      render: (v, r) => {
        if (!v) return '—';
        return (
          <span className="inline-flex items-center gap-2">
            {formatPhone(v)}
            {/* Kullanıcılar ekranındaki ikonun aynısı: metin sunucuda
                hazırlanıp WhatsApp'a taşınıyor, GÖNDER'e kişi basıyor —
                uygulama kimseye mesaj GÖNDERMİYOR.

                `r.whatsapp` alanını backend yalnızca `users.whatsapp` izni
                olana ve numarası çözülebilen velilere koyuyor; alan yoksa
                ikon da yok. Burada ayrıca izin kontrolü yapmaya gerek yok —
                arayüzde gizlemek yetki değildir, asıl kapı sunucuda. */}
            {r.whatsapp && (
              <Tooltip title="Borç hatırlatma mesajı gönder">
                <a
                  href={`https://wa.me/${r.whatsapp.phone}?text=${encodeURIComponent(r.whatsapp.text)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#25D366', lineHeight: 1 }}
                  aria-label={`${r.parent_name || 'Veli'} için borç hatırlatma mesajı gönder`}
                >
                  <WhatsAppOutlined style={{ fontSize: 15 }} />
                </a>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: 'Kalan Tutar',
      dataIndex: 'total_debt',
      align: 'right',
      ...sortProps('total_debt'),
      render: v => <span className="text-red-500 font-bold">{formatMoney(v)}</span>,
    },
    {
      title: 'Toplam Ders Tutarı',
      dataIndex: 'total_charged',
      align: 'right',
      ...sortProps('total_charged'),
      render: v => formatMoney(v),
    },
    {
      title: 'Yapılan Ödeme',
      dataIndex: 'total_paid',
      align: 'right',
      ...sortProps('total_paid'),
      render: v => <span className="text-green-600">{formatMoney(v)}</span>,
    },
  ];

  if (loading) return <div className="text-center p-8"><Spin /></div>;

  return (
    <div>
      {data && (
        <>
          <Row gutter={16} className="mb-4">
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Borçlu Öğrenci Sayısı"
                  value={data.total_debtors}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Toplam Alacak"
                  value={parseFloat(data.total_debt_sum).toFixed(2)}
                  suffix="₺"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Düğme tablonun ÜSTÜNDE: liste uzun, aşağıya konsaydı 100
              satırlık bir sayfada kaydırmadan görünmezdi. */}
          <div className="mb-2 flex justify-end">
            <Button
              icon={<FileExcelOutlined />}
              onClick={excelIndir}
              loading={indiriliyor}
              disabled={!data.total}
            >
              Excel İndir
            </Button>
          </div>

          <Table
            dataSource={data.debtors}
            rowKey="student_id"
            columns={columns}
            size="small"
            loading={loading}
            // Sayfalama ve siralama tek elden: pagination.onChange ayrica
            // taniml kalsaydi sayfa degisiminde iki istek giderdi.
            onChange={(pag, _filters, nextSorter) => {
              const next = nextSorter?.order
                ? { field: nextSorter.field, order: nextSorter.order }
                : { field: null, order: null };
              const sortDegisti = next.field !== sorter.field || next.order !== sorter.order;
              setSorter(next);
              setPersistedPageSize(pag.pageSize);
              // Siralama degisince ilk sayfaya don - aksi halde kullanici
              // bambaska bir siralamanin 3. sayfasina bakiyor olur.
              fetchData(sortDegisti ? 1 : pag.current, pag.pageSize, next);
            }}
            pagination={{
              current: data.current_page,
              pageSize: data.per_page,
              total: data.total,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (total) => `Toplam ${total} borçlu öğrenci`,
            }}
          />
        </>
      )}
    </div>
  );
};

export default DebtReportTab;
